import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../shared/ui/Modal';
import { forgotPassword, verifyOtp, resetPassword } from '../api';

const STEPS = {
  1: { title: "Forgot your password?", subtitle: "Step 1 of 3 · Enter your account email" },
  2: { title: "Enter verification code", subtitle: "Step 2 of 3 · Enter the code we emailed you" },
  3: { title: "Set a new password", subtitle: "Step 3 of 3 · Choose your new password" },
};

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [resetLoading, setResetLoading] = useState(false);

  // Start or reset countdown when entering OTP step.
  useEffect(() => {
    let timerId;
    if (step === 2) {
      setResendTimer(60);
      timerId = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup interval when unmounting or step changes
    return () => clearInterval(timerId);
  }, [step]);

  // Allow only numeric input for OTP and auto-focus next input
  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (value && isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const isOtpComplete = otp.every(val => val !== '');

  // Simple email validation using regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email submission: validate and call OTP endpoint
  const handleEmailSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(email);
      toast.success("OTP sent successfully.");
      // If OTP was sent successfully, advance to OTP step
      setStep(2);
    } catch (error) {
      console.error("Error sending OTP:", error.response?.data || error.message);
      let message = error.response?.data || "An error occurred.";
      toast.error(`Error sending OTP.${message ? `\n${message}` : ''}`);
    }
    setLoading(false);
  };

  // Handle OTP verification: combine OTP digits, verify, and store token
  const handleOtpVerify = async (e) => {
    e?.preventDefault();
    if (!isOtpComplete) {
      toast.error("Please enter the complete OTP.");
      return;
    }
    const otpValue = otp.join('');
    setOtpVerifying(true);
    try {
      const response = await verifyOtp(email, otpValue);
      toast.success("OTP Verification successful.");
      const token = response.token;
      setOtpToken(token);
      // Move to change password step
      setStep(3);
    } catch (error) {
      console.error("OTP Verification failed:", error.response?.data || error.message);
      let message = error.response?.data || "An error occurred.";
      toast.error(`OTP Verification failed.${message ? `\n${message}` : ''}`);
    }
    setOtpVerifying(false);
  };

  // Handle Resend OTP functionality
  const handleResendOtp = async () => {
    try {
      await forgotPassword(email);
      setResendTimer(60); // reset timer
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Error resending OTP. Please try again.");
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    if (!password || password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setResetLoading(true);
    try {
      await resetPassword(otpToken, password);
      toast.success("Password reset successful.");
      onClose();
      window.location.reload(); // Reload the page to reflect changes
    } catch (error) {
      console.error("Password reset failed:", error.response?.data || error.message);
      toast.error("Password reset failed. Please try again.");
    }
    setResetLoading(false);
  };

  const busy = loading || otpVerifying || resetLoading;

  const footer = (
    <>
      <button type="button" className="ui-btn ui-btn--ghost" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      {step === 1 && (
        <button type="submit" form="forgot-password-form" className="ui-btn ui-btn--primary" disabled={loading}>
          {loading ? "Sending..." : "Next"}
        </button>
      )}
      {step === 2 && (
        <button
          type="submit"
          form="forgot-password-form"
          className="ui-btn ui-btn--primary"
          disabled={!isOtpComplete || otpVerifying}
        >
          {otpVerifying ? "Verifying..." : "Confirm"}
        </button>
      )}
      {step === 3 && (
        <button type="submit" form="forgot-password-form" className="ui-btn ui-btn--primary" disabled={resetLoading}>
          {resetLoading ? "Submitting..." : "Submit"}
        </button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={STEPS[step].title}
      subtitle={STEPS[step].subtitle}
      size="sm"
      dismissible={!busy}
      footer={footer}
    >
      <form
        id="forgot-password-form"
        className="ui-form"
        onSubmit={step === 1 ? handleEmailSubmit : step === 2 ? handleOtpVerify : handleResetPassword}
      >
        {step === 1 && (
          <div className="ui-field">
            <label className="ui-label" htmlFor="forgot-password-email">Email address</label>
            <input
              id="forgot-password-email"
              type="email"
              className="ui-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="ui-hint">
              Please enter the account for which you want to reset the password.
            </p>
          </div>
        )}

        {step === 2 && (
          <>
            <p className="ui-hint">
              We have sent a verification code to <strong>{email}</strong>. Please enter it below.
            </p>
            <div className="ui-otp">
              {otp.map((data, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={data}
                  onChange={(e) => handleOtpChange(e, index)}
                  className="ui-input ui-otp__digit"
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>
            <div className="ui-otp__resend">
              {resendTimer > 0 ? (
                <p className="ui-hint">Resend OTP in {resendTimer} seconds</p>
              ) : (
                <button type="button" className="ui-btn ui-btn--link" onClick={handleResendOtp}>
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="ui-field">
              <label className="ui-label" htmlFor="forgot-password-new">New password</label>
              <div className="ui-input-wrap">
                <input
                  id="forgot-password-new"
                  type={showPassword ? "text" : "password"}
                  className="ui-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="ui-input-wrap__action"
                  onClick={() => setShowPassword((show) => !show)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`ph ${showPassword ? "ph-eye-slash" : "ph-eye"}`} />
                </button>
              </div>
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="forgot-password-confirm">Confirm password</label>
              <input
                id="forgot-password-confirm"
                type={showPassword ? "text" : "password"}
                className="ui-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default ForgotPasswordModal;
